import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED, RouterNavigatedAction } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { AuthenticationFacade } from '@planone/bau365/data-access-authentication';
import * as FeatureTrackingGoogleAnalytics from '@planone/shared/feature-tracking-google-analytics';
import { GoogleAnalyticsService } from '@planone/shared/feature-tracking-google-analytics';
import { DocumentRef, WindowRef } from '@planone/shared/util-browser-api';
import { defer, of, switchMap, tap, withLatestFrom } from 'rxjs';

import * as ScrollActions from './scroll.actions';

@Injectable()
export class ScrollEffects {
  scrollTracked = false;

  initialize$ = createEffect(
    () =>
      defer(() => {
        this.windowRef.nativeWindow.addEventListener('scroll', () => {
          this.trackScrollPosition();
        });

        return of(true);
      }),
    { dispatch: false }
  );

  routing$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ROUTER_NAVIGATED),
        tap((actions: RouterNavigatedAction[] | undefined[]) => {
          this.scrollTracked = false;

          const currentRoute = actions[1]?.payload.routerState;
          const data = this.googleAnalyticsService.extractData(currentRoute);

          if (currentRoute && !data?.googleAnalytics?.customTracking) {
            this.trackScrollPosition();
          }
        })
      ),
    { dispatch: false }
  );

  scroll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ScrollActions.scroll),
      withLatestFrom(this.authenticationFacade.excludeAnalytics$),
      switchMap(([action, exclude_analytics]) =>
        of(
          FeatureTrackingGoogleAnalytics.push({
            event: 'scroll',
            percent_scrolled: action.scrollPosition,
            context: action.context,
            page_type: action.pageType,
            exclude_analytics: exclude_analytics,
          })
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private store: Store,
    private authenticationFacade: AuthenticationFacade,
    private googleAnalyticsService: GoogleAnalyticsService,
    private windowRef: WindowRef,
    private documentRef: DocumentRef,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  private trackScrollPosition() {
    const scrollPercent = this.getScrollPercent();

    if (scrollPercent >= 90 && !this.scrollTracked) {
      const data = this.googleAnalyticsService.extractData(this.route.snapshot);
      const pageType = data?.analyticsPageType ?? this.googleAnalyticsService.getPageType(this.router.url);

      this.store.dispatch(
        ScrollActions.scroll({
          pageType: pageType,
          scrollPosition: scrollPercent,
          context: this.route.snapshot.params['fair_id'],
        })
      );

      this.scrollTracked = true;
    }
  }

  private getScrollPercent() {
    const h = this.documentRef.nativeDocument.documentElement,
      b = this.documentRef.nativeDocument.body,
      st = 'scrollTop',
      sh = 'scrollHeight';

    return ((h[st] || b[st]) / ((h[sh] || b[sh]) - h.clientHeight)) * 100;
  }
}
